import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  VStack,
  Text,
  Box,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Divider
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, LockIcon, WarningIcon } from '@chakra-ui/icons';
// Removed password strength validation for simplified requirements

interface PasswordSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPasswordSet: (password: string) => Promise<void>;
  onExport?: () => void;
  title?: string;
  showBackupWarning?: boolean;
}

const PasswordSetupModal: React.FC<PasswordSetupModalProps> = ({
  isOpen,
  onClose,
  onPasswordSet,
  onExport,
  title = 'Add Password Protection',
  showBackupWarning = true
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Please enter a password');
      return;
    }

    if (password.length < 4) {
      setError('Password must be at least 4 characters long');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onPasswordSet(password);
      setPassword('');
      setConfirmPassword('');
      onClose();
    } catch (error) {
      console.error('[PasswordSetupModal] Error setting password:', error);
      setError('Failed to set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [password, confirmPassword, passwordsMatch, onPasswordSet, onClose, toast]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setPassword('');
      setConfirmPassword('');
      setError(null);
      onClose();
    }
  }, [isLoading, onClose]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit(e);
    }
  }, [handleSubmit, isLoading]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent maxW="lg" mx={4}>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2}>
            <LockIcon color="blue.500" />
            {title}
          </Box>
        </ModalHeader>
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            {showBackupWarning && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertDescription>
                    You may wish to consider backing up this Deck.
                    {onExport && (
                      <Box display="flex" justifyContent="center" mt={2}>
                        <Button
                          onClick={onExport}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                        >
                          Export Deck
                        </Button>
                      </Box>
                    )}
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <Box width="full">
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Password
                  </Text>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      isDisabled={isLoading}
                      pr="4.5rem"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="sm"
                        isDisabled={isLoading}
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>

                <Box width="full">
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Confirm Password
                  </Text>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      isDisabled={isLoading}
                      pr="4.5rem"
                      borderColor={confirmPassword && !passwordsMatch ? 'red.300' : undefined}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        variant="ghost"
                        size="sm"
                        isDisabled={isLoading}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {confirmPassword && !passwordsMatch && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      Passwords do not match
                    </Text>
                  )}
                </Box>
              </VStack>
            </form>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Copying link..."
            isDisabled={
              !password.trim() || 
              !passwordsMatch || 
              isLoading
            }
          >
            Copy Link
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PasswordSetupModal;
